import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { ButtonBase, ListItemText, Menu, MenuItem, Typography } from "@mui/material";

import { useEffect, useRef, useState } from "react";

import { getStoredAuthLocale, setStoredAuthLocale } from "@/lib/auth/preferredLocale";
import { updateUserPreferences } from "@/slices/auth/authThunk";
import { useDispatch, useSelector } from "@/store";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

const languages = [
  { code: "en", name: "English" },
  // { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "한국어" }
  // { code: "vi", name: "Tiếng Việt", flag: "🇻🇳" },
  // { code: "zh", name: "中文", flag: "🇨🇳" },
  // { code: "ae", name: "العربية", flag: "🇦🇪" }
];

const supportedLanguageCodes = new Set(languages.map((language) => language.code));

const normalizeLocale = (locale?: string | null) => locale?.split("-")[0]?.toLowerCase();

const resolveBrowserLanguage = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const candidates = [window.navigator.languages?.[0], window.navigator.language].filter(
    (candidate): candidate is string => typeof candidate === "string" && candidate.trim().length > 0
  );

  for (const candidate of candidates) {
    const normalized = normalizeLocale(candidate);
    if (normalized && supportedLanguageCodes.has(normalized)) {
      return normalized;
    }
  }

  return undefined;
};

const resolveBaseLanguage = (
  routerLocale?: string,
  defaultLocale?: string,
  userLanguage?: string,
  storedLanguage?: string
) => {
  const normalizedRouterLocale = normalizeLocale(routerLocale);
  const normalizedDefaultLocale = normalizeLocale(defaultLocale) || "en";

  if (
    normalizedRouterLocale &&
    supportedLanguageCodes.has(normalizedRouterLocale) &&
    normalizedRouterLocale !== normalizedDefaultLocale
  ) {
    return normalizedRouterLocale;
  }

  const normalizedUserLanguage = normalizeLocale(userLanguage);
  if (normalizedUserLanguage && supportedLanguageCodes.has(normalizedUserLanguage)) {
    return normalizedUserLanguage;
  }

  const normalizedStoredLanguage = normalizeLocale(storedLanguage);
  if (normalizedStoredLanguage && supportedLanguageCodes.has(normalizedStoredLanguage)) {
    return normalizedStoredLanguage;
  }

  if (normalizedRouterLocale && supportedLanguageCodes.has(normalizedRouterLocale)) {
    return normalizedRouterLocale;
  }

  if (normalizedDefaultLocale && supportedLanguageCodes.has(normalizedDefaultLocale)) {
    return normalizedDefaultLocale;
  }

  return "en";
};

const resolveBrowserPreferredLanguage = (
  routerLocale?: string,
  defaultLocale?: string,
  userLanguage?: string,
  storedLanguage?: string
) => {
  const baseLanguage = resolveBaseLanguage(routerLocale, defaultLocale, userLanguage, storedLanguage);
  const normalizedUserLanguage = normalizeLocale(userLanguage);
  const normalizedStoredLanguage = normalizeLocale(storedLanguage);

  if (
    (normalizedUserLanguage && supportedLanguageCodes.has(normalizedUserLanguage)) ||
    (normalizedStoredLanguage && supportedLanguageCodes.has(normalizedStoredLanguage))
  ) {
    return baseLanguage;
  }

  const normalizedRouterLocale = normalizeLocale(routerLocale);
  const normalizedDefaultLocale = normalizeLocale(defaultLocale) || "en";
  const browserLanguage = resolveBrowserLanguage();

  if (browserLanguage && normalizedRouterLocale === normalizedDefaultLocale) {
    return browserLanguage;
  }

  return baseLanguage;
};

export const LanguageSwitcher = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth?.user);
  const { t } = useTranslation();
  const syncedGuestLocale = useRef<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    resolveBaseLanguage(router.locale, router.defaultLocale, user?.preferences?.language, getStoredAuthLocale())
  );
  const selectedLanguageName = languages.find((language) => language.code === selectedLanguage)?.name || "English";

  useEffect(() => {
    const userLanguage = normalizeLocale(user?.preferences?.language);
    const storedLanguage = getStoredAuthLocale();

    if (userLanguage && supportedLanguageCodes.has(userLanguage)) {
      setStoredAuthLocale(userLanguage);
      syncedGuestLocale.current = null;
    } else if (user?.id && storedLanguage) {
      const syncKey = `${user.id}:${storedLanguage}`;
      if (syncedGuestLocale.current !== syncKey) {
        syncedGuestLocale.current = syncKey;
        void dispatch(updateUserPreferences({ language: storedLanguage }))
          .unwrap()
          .catch(() => {
            if (syncedGuestLocale.current === syncKey) {
              syncedGuestLocale.current = null;
            }
          });
      }
    }

    const preferredLanguage = resolveBrowserPreferredLanguage(
      router.locale,
      router.defaultLocale,
      user?.preferences?.language,
      storedLanguage
    );
    setSelectedLanguage(preferredLanguage);

    const normalizedRouterLocale = normalizeLocale(router.locale) || normalizeLocale(router.defaultLocale) || "en";
    const normalizedDefaultLocale = normalizeLocale(router.defaultLocale) || "en";
    if (normalizedRouterLocale === normalizedDefaultLocale && preferredLanguage !== normalizedDefaultLocale) {
      void router.replace(router.pathname, router.asPath, { locale: preferredLanguage });
    }
  }, [
    dispatch,
    router.asPath,
    router.defaultLocale,
    router.locale,
    router.pathname,
    user?.id,
    user?.preferences?.language
  ]);

  const handleLanguageChange = async (newLocale: string) => {
    setAnchorEl(null);
    setSelectedLanguage(newLocale);
    setStoredAuthLocale(newLocale);

    if (user?.id) {
      try {
        await dispatch(updateUserPreferences({ language: newLocale })).unwrap();
      } catch (err) {
        // ignore
      }
    }

    // Change the locale while preserving the current path
    router.push(router.pathname, router.asPath, { locale: newLocale });
  };

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={`${t("Language")}: ${selectedLanguageName}`}
        aria-haspopup="menu"
        aria-expanded={anchorEl ? "true" : undefined}
        sx={{
          minHeight: 36,
          borderRadius: 1,
          px: 1,
          color: "text.primary",
          gap: 0.5,
          "&:hover": {
            bgcolor: "action.hover"
          },
          "&:focus-visible": {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2
          }
        }}
      >
        <LanguageOutlinedIcon fontSize="small" aria-hidden="true" />
        <Typography component="span" variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>
          {selectedLanguageName}
        </Typography>
        <ExpandMoreIcon fontSize="small" aria-hidden="true" />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ "aria-label": t("Language options") }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === selectedLanguage}
            onClick={() => void handleLanguageChange(lang.code)}
          >
            <ListItemText primary={lang.name} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
